import { useEffect, useEffectEvent, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const FIRST_FIELD = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

/** UI a field inside the panel opens over the page, such as the rich text editor's dialogs. */
const OVERLAY_LAYER = '.tox-tinymce-aux, .tox-dialog-wrap';

interface DialogProps {
  title: string;
  /** Called by Escape and the close button. Neither works while `busy`. */
  onClose: () => void;
  /** A request is running; the dialog can't be dismissed until it settles. */
  busy?: boolean;
  /** Panel width; 500px by default. */
  widthClass?: string;
  children: ReactNode;
}

/** Keeps Tab and Shift+Tab inside the panel. */
function trapFocus(event: KeyboardEvent, panel: HTMLElement) {
  const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
  const first = items.at(0);
  const last = items.at(-1);
  const active = document.activeElement;
  if (!first || !last) {
    event.preventDefault();
    panel.focus();
  } else if (!panel.contains(active)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Modal dialog, open while it is rendered: `{editing && <Dialog …/>}`. It is labelled by its
 * title, keeps focus inside, closes on Escape, and returns focus to whatever opened it.
 * Focus starts on `[data-autofocus]`, else the first field, else the panel.
 *
 * Built by hand rather than on `<dialog>`: the test DOM (jsdom) has no `showModal`.
 */
export function Dialog({
  title,
  onClose,
  busy = false,
  widthClass = 'max-w-[500px]',
  children,
}: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    // Escape and Tab there belong to that layer (Escape closes it), not to this dialog.
    if (event.target instanceof Element && event.target.closest(OVERLAY_LAYER)) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (!busy) onClose();
    } else if (event.key === 'Tab') {
      trapFocus(event, panel);
    }
  });

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const start =
      panel?.querySelector<HTMLElement>('[data-autofocus]') ??
      panel?.querySelector<HTMLElement>(FIRST_FIELD) ??
      panel;
    start?.focus();

    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const listener = (event: KeyboardEvent) => onKeyDown(event);
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
      document.body.style.overflow = scroll;
      opener?.focus();
    };
  }, []);

  return createPortal(
    // `my-auto` (not `items-center`) centres a short panel yet lets a tall one scroll from its top.
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'my-8 w-full rounded-card bg-white p-5 shadow-card outline-none sm:my-auto',
          widthClass,
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-lg font-medium text-ink-strong">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="-m-1 rounded-md p-1 text-muted hover:bg-canvas hover:text-ink disabled:opacity-50"
          >
            <X className="size-5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Right-aligned button row at the bottom of a dialog. */
export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="mt-6 flex flex-wrap justify-end gap-3">{children}</div>;
}
